import React, { useMemo, useRef } from "react"
import { Modal } from "antd"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { shownNames } from "../../model/shownLabels"
import type { ShownLabel, TableSpec } from "../../types/tabSpecDocument"

/** How the review names a table: its own title, else its question (and grid row or loop) as shown. */
const removedName = (table: TableSpec, labels: ShownLabel[] | undefined): string => {
  const title = table.title?.trim()
  if (title) return title
  const names = shownNames(labels)
  const question = names.variable(table.question)
  if (table.kind === "grid-summary") return `${question} summary`
  return table.instance
    ? `${question} - ${names.instance(table.question, table.instance, table.instance)}`
    : question
}

export const REMOVED_TITLE = "The assistant's tables remove some of yours"

/**
 * Before an assistant proposal that drops tables of the open spec goes into
 * the draft: which tables it removes, then Cancel or "Load into the draft".
 * Open while `removed` is set. Renders inside the spec, so it gets the
 * --qc-* tokens.
 */
const RemovedTablesReview: React.FC<{
  removed: TableSpec[] | null
  /** The open spec's shown labels, to name the tables as the spec shows them. */
  labels: ShownLabel[] | undefined
  onLoad: () => void
  onCancel: () => void
}> = ({ removed, labels, onLoad, onCancel }) => {
  const anchorRef = useRef<HTMLSpanElement>(null)
  const container = (): HTMLElement =>
    anchorRef.current?.closest<HTMLElement>(".tabspec-root") ?? window.document.body
  const names = useMemo(
    () => (removed ?? []).map((t) => removedName(t, labels)),
    [removed, labels]
  )
  const n = names.length
  return (
    <>
      <span ref={anchorRef} hidden />
      <Modal
        open={removed !== null}
        title={REMOVED_TITLE}
        onCancel={onCancel}
        getContainer={container}
        destroyOnHidden
        footer={[
          <DataQButton key="cancel" type="secondary" onClick={onCancel}>
            Cancel
          </DataQButton>,
          <DataQButton key="load" type="primary" onClick={onLoad}>
            Load into the draft
          </DataQButton>,
        ]}
      >
        <p>
          {n === 1 ? "This table is" : `These ${n} tables are`} not in the assistant&apos;s
          version, so loading it removes {n === 1 ? "it" : "them"} from the draft:
        </p>
        <ul className="m-0 pl-4 list-disc" aria-label="Tables removed">
          {names.map((name, i) => (
            <li key={i}>{name}</li>
          ))}
        </ul>
        <p className="mt-2 text-xs text-gray-500">
          Nothing is saved until you save. Undo on the proposal puts the draft back.
        </p>
      </Modal>
    </>
  )
}

export default RemovedTablesReview
